import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import { activateDeviceSchema } from '@shripos/shared/schemas';
import { Alert, Button, Field, Input, errorAt } from '@shripos/ui';
import { api } from '../../lib/api.js';
import { useSession } from '../../stores/session.js';
import { AuthShell } from '../auth/AuthPages.jsx';

/** Best-effort, non-identifying description of this device for the admin's device list. */
function deviceInfo() {
  const ua = navigator.userAgent;
  const os = /Android/i.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua)
      ? 'iOS'
      : /Windows/i.test(ua)
        ? 'Windows'
        : /Mac OS/i.test(ua)
          ? 'macOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : 'Other';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Other';
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches;
  return {
    os,
    browser,
    platform: standalone ? 'installed-pwa' : 'browser',
    screen: `${window.screen.width}x${window.screen.height}`,
  };
}

export function ActivatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = useSession((s) => s.device);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const { register, handleSubmit, formState } = useForm({
    resolver: zodResolver(activateDeviceSchema),
    defaultValues: { code: '', name: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await api.post('/devices/activate', { ...values, info: deviceInfo() });
      const current = await api.get('/devices/current');
      useSession.getState().setDevice(current);
      setResult(res.device);
    } catch (err) {
      setError(err.message);
    }
  });

  if (result) {
    return (
      <AuthShell title={t('device.activateTitle')}>
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="size-12 text-emerald-600" />
          <p className="text-slate-700">
            {t('device.activated', { name: result.name, code: result.code, outlet: result.outletName })}
          </p>
          {result.kind === 'self_order' ? (
            <Button size="lg" className="mt-2 w-full" onClick={() => navigate('/self-order', { replace: true })}>
              Start ordering mode
            </Button>
          ) : (
            <Button size="lg" className="mt-2 w-full" onClick={() => navigate('/pin', { replace: true })}>
              {t('pin.title')}
            </Button>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('device.activateTitle')}
      subtitle={t('device.activateSubtitle')}
      footer={
        <Link to="/login" className="text-brand-700 hover:underline">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      {existing && (
        <Alert tone="info" className="mb-4">
          This browser is already registered as {existing.device.name} ({existing.device.code}). Registering again adds
          a new device; ask a manager to revoke the old one.
        </Alert>
      )}
      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t('device.code')} error={errorAt(formState.errors, 'code')}>
          <Input
            size="lg"
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="XXXX-XXXX"
            className="text-center font-mono text-2xl tracking-[0.3em] uppercase"
            maxLength={9}
            {...register('code')}
          />
        </Field>
        <Field label={t('device.name')} error={errorAt(formState.errors, 'name')}>
          <Input size="lg" placeholder={t('device.namePlaceholder')} {...register('name')} />
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
          {t('device.activate')}
        </Button>
      </form>
    </AuthShell>
  );
}
